import { useEffect } from 'react';

import { useSessionValue } from 'appLayer/auth';
import { useStoreValue } from 'state/hooks/useStore';
import { GAEventName } from 'analytics/ga-event-name.enum';
import { VARIANT_BANNER } from 'constants/products';
import { createAnalyticsEvent } from 'analytics/create-analytics-event';
import { useV53AccountIdentifier } from '@portal/state/hooks/useV53AccountIdentifier';
import { SubscriptionDetailsV2Response } from 'state/internal/getSubscriptionDetails/types';
import { isSinglePouchSubVariant } from '../utils';
import { PromotionalBanner } from './subscriptions.types';

interface Response {
  hasCompatibleSubscription: boolean;
  bannerData?: PromotionalBanner;
}

type ListResponse = Response & {
  targetSubscriptions?: SubscriptionDetailsV2Response[]; // only return this field for subscription list
};

// determines whether the current subscription details page can display promotional banner
// current sub page must be a Single AG1 Pouch subscription
export const useSubscriptionDetailsPromotionV2 = ({
  subscription,
  promotionalBannerData
}: {
  subscription: SubscriptionDetailsV2Response | undefined;
  promotionalBannerData: PromotionalBanner[] | null | undefined;
}): Response => {
  const session = useSessionValue();
  const storeCode = useStoreValue();
  const isV53Account = useV53AccountIdentifier();

  let hasCompatibleSubscription = false;

  subscription?.line_items.forEach((lineItem) => {
    const isSinglePouch = isSinglePouchSubVariant(lineItem.product.shopify_variant_id, storeCode);

    if (isSinglePouch && lineItem.quantity === 1 && !lineItem.is_one_time) {
      hasCompatibleSubscription = true;
    }
  });

  const response = (() => {
    if (!promotionalBannerData?.length || !hasCompatibleSubscription) {
      return {
        hasCompatibleSubscription: false
      };
    }

    const defaultBannerV53 = promotionalBannerData.find((i) => i.isDefault);
    const defaultBannerV52 = promotionalBannerData.find((i) => i.isDefaultV52);
    const bannerData = isV53Account ? defaultBannerV53 : defaultBannerV52;

    return {
      hasCompatibleSubscription: true,
      bannerData
    };
  })();

  useEffect(() => {
    // Only log if we want something other than the baseline.
    if (response.hasCompatibleSubscription) {
      const promoteQty2Analytics = createAnalyticsEvent(() => ({
        date: Date.now().toString(),
        event: GAEventName.TEST_COLLECTION,
        location: 'Bulk Buy banner / modal',
        shopify_id: `${session?.customer?.id}`,
        test_variant: response.hasCompatibleSubscription ? VARIANT_BANNER : 'fallout'
      }));
      promoteQty2Analytics();
    }

    // Track this only once.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return response;
};

// determines whether the subscription list page can display promotional banner
// the subscription that will be targeted is the first sub w/ Single AG1 Pouch
export const useSubscriptionListPromotionV2 = ({
  subscriptions,
  promotionalBannerData
}: {
  subscriptions: SubscriptionDetailsV2Response[] | undefined;
  promotionalBannerData: PromotionalBanner[] | null | undefined;
}): ListResponse => {
  const session = useSessionValue();
  const storeCode = useStoreValue();
  const isV53Account = useV53AccountIdentifier();

  const subscriptionsWithSinglePouch: SubscriptionDetailsV2Response[] = [];

  subscriptions?.forEach((subscription) => {
    const hasSinglePouchItem = Boolean(
      subscription.line_items.filter(
        (lineItem) =>
          isSinglePouchSubVariant(lineItem.product.shopify_variant_id, storeCode) &&
          lineItem?.quantity === 1 &&
          !lineItem.is_one_time
      ).length
    );

    if (hasSinglePouchItem) subscriptionsWithSinglePouch.push(subscription);
  });

  const hasCompatibleSubscription = Boolean(subscriptionsWithSinglePouch?.length);

  const response = (() => {
    if (!promotionalBannerData?.length || !hasCompatibleSubscription) {
      return {
        hasCompatibleSubscription: false
      };
    }

    const defaultBannerV53 = promotionalBannerData.find((i) => i.isDefault);
    const defaultBannerV52 = promotionalBannerData.find((i) => i.isDefaultV52);
    const bannerData = isV53Account ? defaultBannerV53 : defaultBannerV52;

    return {
      hasCompatibleSubscription: true,
      bannerData,
      targetSubscriptions: subscriptionsWithSinglePouch
    };
  })();

  useEffect(() => {
    // Only log if we want something other than the baseline.
    if (!response.hasCompatibleSubscription) {
      const promoteQty2Analytics = createAnalyticsEvent(() => ({
        date: Date.now().toString(),
        event: GAEventName.TEST_COLLECTION,
        location: 'Bulk Buy banner / modal',
        shopify_id: `${session?.customer?.id}`,
        test_variant: response.hasCompatibleSubscription ? VARIANT_BANNER : 'fallout'
      }));
      promoteQty2Analytics();
    }

    // Track this only once.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return response;
};
